import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { validateApiResponse } from '../../../services';
import { getMemberRemovalImpact } from '../../../services/membersService';
import { buildRemovalSummary } from '../utils/removalSummary';

export const REMOVE_STEP = Object.freeze({ LOSES: 1, CONFIRM: 2 });

const INITIAL = { step: REMOVE_STEP.LOSES, emailInput: '' };

const sameEmail = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * State of the two-step Remove flow: step 1 "What they lose" (summary loaded when the dialog
 * opens), step 2 type the member's email (trimmed, case-insensitive) to enable Remove.
 * `onRemove` resolves `true` when the member was removed (the dialog closes) and `false` when
 * it failed (the dialog stays on step 2; the caller already showed the error toast).
 *
 * @param {object} args
 * @param {{ id: string, email: string }} args.member UI member
 * @param {boolean} args.isOpen
 * @param {() => Promise<boolean>} args.onRemove
 * @param {() => void} args.onClose
 */
export const useRemoveMemberFlow = ({ member, isOpen, onRemove, onClose }) => {
  const [form, setForm] = useState(INITIAL);
  const [data, setData] = useState({ summary: [], isLoadingSummary: true, summaryError: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  // The ref blocks a double click before React re-renders with `isSubmitting`.
  const submittingRef = useRef(false);

  const { id: memberId, email } = member;

  // Each opening starts again on step 1 (state adjusted during render, not in an effect).
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    setForm(INITIAL);
    if (isOpen) setData({ summary: [], isLoadingSummary: true, summaryError: false });
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchIdRef.current += 1;
    const fetchId = fetchIdRef.current;
    if (!isOpen) return;
    getMemberRemovalImpact(memberId)
      .then((res) => validateApiResponse(res, 'Could not load the removal summary'))
      .then(({ body }) => ({ summary: buildRemovalSummary(body), isLoadingSummary: false, summaryError: false }))
      .catch(() => ({ summary: [], isLoadingSummary: false, summaryError: true }))
      .then((next) => {
        if (mountedRef.current && fetchId === fetchIdRef.current) setData(next);
      });
  }, [isOpen, memberId]);

  const next = useCallback(() => setForm((f) => ({ ...f, step: REMOVE_STEP.CONFIRM })), []);
  // Back keeps what was typed: Continue again returns to the same email (CT-5828).
  const back = useCallback(() => setForm((f) => ({ ...f, step: REMOVE_STEP.LOSES })), []);
  const setEmailInput = useCallback((emailInput) => setForm((f) => ({ ...f, emailInput })), []);

  const isEmailMatch = Boolean(email) && sameEmail(form.emailInput, email);

  const confirm = useCallback(async () => {
    if (!isEmailMatch || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    let ok = false;
    try {
      ok = await onRemove();
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setIsSubmitting(false);
    }
    if (ok) onClose();
  }, [isEmailMatch, onRemove, onClose]);

  return useMemo(
    () => ({ ...form, ...data, isEmailMatch, isSubmitting, next, back, setEmailInput, confirm }),
    [form, data, isEmailMatch, isSubmitting, next, back, setEmailInput, confirm],
  );
};
